import React from "react";
import RoleToggle from "./RoleToggle";
import { TabConfig } from "../hooks/usePermissionTabs";
import { useLanguage } from "../context/LanguageContext";

interface Props {
  title?: string;
  visibleTabs: TabConfig[];
  activeLabel: string;
  content: React.ReactNode;
  isLoading: boolean;
  error: any;
  handleChange: (label: string) => void;
  className?: string;
  subClassName?: string;
  contentClassName?: string;
  loadingHeight?: string;
  headerButtons?: React.ReactNode;
  tabPosition?: "left" | "right";
  /** Forwarded active-item style to RoleToggle */
  activeClassName?: string;
  /** Hide the tab toggle row entirely (parent already owns tab switching) */
  hideToggle?: boolean;
  /** Right-panel / side-drawer content */
  rightPanel?: React.ReactNode;
  /** Whether the right panel is visible (controls col-span split) */
  showRightPanel?: boolean;
  /** Backdrop / overlay rendered between content and right panel in DOM */
  rightOverlay?: React.ReactNode;
  /** Extra classes applied to the grid wrapper when rightPanel is used */
  contentGridClassName?: string;
}

export const PermissionTabManager: React.FC<Props> = ({
  title,
  visibleTabs,
  activeLabel,
  content,
  isLoading,
  error,
  handleChange,
  className = "px-6 space-y-4",
  subClassName = "space-y-0",
  contentClassName = "pb-6",
  loadingHeight = "h-64",
  headerButtons,
  tabPosition = "left",
  activeClassName,
  hideToggle = false,
  rightPanel,
  showRightPanel = false,
  rightOverlay,
  contentGridClassName = "",
}) => {
  const { t } = useLanguage();

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={className}>
        {title && (
          <div className="flex justify-between items-center">
            <h1 className="text-2xl text-[#212121] tracking-[-0.5px]">
              {t(title)}
            </h1>
            <div className="w-64 h-10 bg-gray-200 rounded animate-pulse" />
          </div>
        )}
        <div className="bg-white rounded-xl p-6">
          <div
            className={`${loadingHeight} bg-gray-100 rounded animate-pulse`}
          />
        </div>
      </div>
    );
  }

  // ── No-access / error state ────────────────────────────────────────────────
  if (error || visibleTabs.length === 0) {
    return (
      <div className={className}>
        {title && (
          <div className="flex justify-between items-center">
            <h1 className="text-2xl text-[#212121] tracking-[-0.5px]">
              {t(title)}
            </h1>
          </div>
        )}
        <div className="bg-white rounded-xl p-6 text-center text-gray-500">
          {error ? t("Failed to load permissions.") : t("No access to any section.")}
        </div>
      </div>
    );
  }

  // ── Content area: grid layout when rightPanel provided, simple div otherwise
  const contentArea =
    rightPanel !== undefined ? (
      <div className={`relative grid grid-cols-12 ${contentGridClassName}`}>
        <div
          className={`${
            showRightPanel ? "col-span-12 lg:col-span-7" : "col-span-12"
          } ${contentClassName}`}
        >
          {content}
        </div>
        {rightOverlay}
        {showRightPanel && rightPanel}
      </div>
    ) : (
      <div className={contentClassName}>{content}</div>
    );

  // ── Normal render ─────────────────────────────────────────────────────────
  return (
    <div className={className}>
      <div className={subClassName}>
        {/* Tab toggle row — hidden when hideToggle=true */}
        {!hideToggle && (
          <div className="flex justify-between items-center flex-wrap lg:flex-nowrap w-full">
            {tabPosition === "right" ? (
              <>
                {title && (
                  <h1 className="text-2xl text-[#212121] tracking-[-0.5px] mb-3 lg:mb-0">
                    {t(title)}
                  </h1>
                )}
                <div className="flex items-center gap-4 flex-wrap lg:flex-nowrap ml-auto">
                  <RoleToggle
                    containerClassName=""
                    options={visibleTabs.map((t) => t.label)}
                    active={activeLabel}
                    onChange={handleChange}
                    activeClassName={activeClassName}
                  />
                  {headerButtons && (
                    <div className="flex items-center gap-2 pr-4 lg:pr-8">
                      {headerButtons}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-4 flex-wrap lg:flex-nowrap">
                  {title && (
                    <h1 className="text-2xl text-[#212121] tracking-[-0.5px] mb-3 lg:mb-0">
                      {t(title)}
                    </h1>
                  )}
                  <RoleToggle
                    options={visibleTabs.map((t) => t.label)}
                    active={activeLabel}
                    onChange={handleChange}
                    activeClassName={activeClassName}
                  />
                </div>
                {headerButtons && (
                  <div className="flex items-center gap-2 pr-3">
                    {headerButtons}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {contentArea}
      </div>
    </div>
  );
};
